import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  IconButton,
  Tooltip,
  Switch,
  Stack,
  Textarea,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { FaCreditCard, FaMoneyBillWave, FaPaypal } from 'react-icons/fa';
import Card from 'components/card/Card.js';
import { getData, postData, putData, deleteData } from '../../../utils/httpClient';
import { useSearch } from '../../../contexts/SearchContext';

export default function PaymentMethods() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  
  const { searchQuery } = useSearch();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    descriptionLong: '',
    installments: '',
    enabled: true,
    discountPercentage: '',
    requiresFullPayment: false,
    installmentsAllowed: true,
  });

  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const fetchPaymentMethods = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getData('/payment-methods');
      console.log('Payment methods response:', response);
      
      // Asegurar que la respuesta sea un array
      if (Array.isArray(response)) {
        setPaymentMethods(response);
      } else if (response && Array.isArray(response.data)) {
        setPaymentMethods(response.data);
      } else if (response && Array.isArray(response.payload)) {
        setPaymentMethods(response.payload);
      } else {
        console.warn('Response is not an array:', response);
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      setPaymentMethods([]); // Asegurar que sea un array vacío en caso de error
      toast({
        title: 'Error',
        description: 'Error al cargar las formas de pago',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const handleCreatePaymentMethod = () => {
    setModalMode('create');
    setSelectedPaymentMethod(null);
    setFormData({
      name: '',
      description: '',
      descriptionLong: '',
      installments: '',
      enabled: true,
      discountPercentage: '',
      requiresFullPayment: false,
      installmentsAllowed: true,
    });
    setIsModalOpen(true);
  };

  const handleEditPaymentMethod = (paymentMethod) => {
    setModalMode('edit');
    setSelectedPaymentMethod(paymentMethod);
    setFormData({
      name: paymentMethod.name || '',
      description: paymentMethod.description || '',
      descriptionLong: paymentMethod.descriptionLong || '',
      installments: paymentMethod.installments || '',
      enabled: paymentMethod.enabled !== undefined ? paymentMethod.enabled : true,
      discountPercentage: paymentMethod.discountPercentage || '',
      requiresFullPayment: paymentMethod.requiresFullPayment || false,
      installmentsAllowed: paymentMethod.installmentsAllowed !== undefined ? paymentMethod.installmentsAllowed : true,
    });
    setIsModalOpen(true);
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta forma de pago?')) {
      try {
        await deleteData(`/payment-methods/${paymentMethodId}`);
        toast({
          title: 'Éxito',
          description: 'Forma de pago eliminada correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchPaymentMethods();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Error al eliminar la forma de pago',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const paymentMethodData = {
        name: formData.name,
        description: formData.description,
        description_long: formData.descriptionLong,
        Installments: parseInt(formData.installments) || 1,
        discount_percentage: parseFloat(formData.discountPercentage) || 0,
        requires_full_payment: formData.requiresFullPayment,
        installments_allowed: formData.installmentsAllowed,
        enabled: formData.enabled,
      };

      if (modalMode === 'create') {
        await postData('/payment-methods', paymentMethodData);
        toast({
          title: 'Éxito',
          description: 'Forma de pago creada correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await putData(`/payment-methods/${selectedPaymentMethod.id}`, paymentMethodData);
        toast({
          title: 'Éxito',
          description: 'Forma de pago actualizada correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      setIsModalOpen(false);
      fetchPaymentMethods();
    } catch (error) {
      toast({
        title: 'Error',
        description: `Error al ${modalMode === 'create' ? 'crear' : 'actualizar'} la forma de pago`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredPaymentMethods = (Array.isArray(paymentMethods) ? paymentMethods : []).filter((method) =>
    searchQuery === '' ||
    method.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    method.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    method.descriptionLong?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card
        direction="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Flex px="25px" justify="space-between" mb="20px" align="center">
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Planes de Pago
          </Text>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="brand"
            variant="solid"
            onClick={handleCreatePaymentMethod}
          >
            Agregar Plan de Pago
          </Button>
        </Flex>

        {/* Tabla de formas de pago */}
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead>
            <Tr>
              <Th borderColor={borderColor}>Nombre</Th>
              <Th borderColor={borderColor}>Descripción</Th>
              <Th borderColor={borderColor}>Cuotas</Th>
              <Th borderColor={borderColor}>Descuento</Th>
              <Th borderColor={borderColor}>Tipo de Pago</Th>
              <Th borderColor={borderColor}>Estado</Th>
              <Th borderColor={borderColor}>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredPaymentMethods.map((method) => (
              <Tr 
                key={method.id}
                cursor="pointer"
                _hover={{ bg: hoverBg }}
              >
                <Td borderColor={borderColor}>
                  <Box>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {method.name || method.description}
                    </Text>
                    {method.descriptionLong && (
                      <Text color="gray.500" fontSize="xs" noOfLines={2}>
                        {method.descriptionLong}
                      </Text>
                    )}
                  </Box>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {method.description}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {method.installments || 1} cuotas
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {method.discountPercentage ? `${method.discountPercentage}%` : 'Sin descuento'}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Box>
                    {method.requiresFullPayment ? (
                      <Badge colorScheme="red">Pago completo</Badge>
                    ) : method.installmentsAllowed ? (
                      <Badge colorScheme="green">Permite cuotas</Badge>
                    ) : (
                      <Badge colorScheme="gray">Pago único</Badge>
                    )}
                  </Box>
                </Td>
                <Td borderColor={borderColor}>
                  <Badge colorScheme={method.enabled ? 'green' : 'red'}>
                    {method.enabled ? 'Activo' : 'Inactivo'}
                  </Badge>
                </Td>
                <Td borderColor={borderColor}>
                  <Flex gap="2">
                    <Tooltip label="Editar forma de pago">
                      <IconButton
                        icon={<EditIcon />}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPaymentMethod(method);
                        }}
                      />
                    </Tooltip>
                    <Tooltip label="Eliminar forma de pago">
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePaymentMethod(method.id);
                        }}
                      />
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {filteredPaymentMethods.length === 0 && !loading && (
          <Text textAlign="center" py="20px" color="gray.500">
            No se encontraron planes de pago
          </Text>
        )}
      </Card>

      {/* Modal para crear/editar forma de pago */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {modalMode === 'create' ? 'Agregar Plan de Pago' : 'Editar Plan de Pago'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <Flex gap={4}>
                <FormControl>
                  <FormLabel>Nombre</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Ej: Tarjeta de Crédito"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Descripción Corta</FormLabel>
                  <Input
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Descripción corta"
                  />
                </FormControl>
              </Flex>

              <FormControl>
                <FormLabel>Descripción Detallada</FormLabel>
                <Textarea
                  value={formData.descriptionLong}
                  onChange={(e) => handleInputChange('descriptionLong', e.target.value)}
                  placeholder="Descripción detallada del método de pago"
                  resize="vertical"
                />
              </FormControl>

              <Flex gap={4}>
                <FormControl>
                  <FormLabel>Número de Cuotas</FormLabel>
                  <NumberInput
                    value={formData.installments}
                    onChange={(value) => handleInputChange('installments', value)}
                    min={1}
                  >
                    <NumberInputField placeholder="1" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <FormControl>
                  <FormLabel>Porcentaje de Descuento (%)</FormLabel>
                  <NumberInput
                    value={formData.discountPercentage}
                    onChange={(value) => handleInputChange('discountPercentage', value)}
                    min={0}
                    max={100}
                    precision={2}
                  >
                    <NumberInputField placeholder="0.00" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
              </Flex>

              <Stack spacing={3}>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Forma de pago activa</FormLabel>
                  <Switch
                    isChecked={formData.enabled}
                    onChange={(e) => handleInputChange('enabled', e.target.checked)}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Requiere pago completo</FormLabel>
                  <Switch
                    isChecked={formData.requiresFullPayment}
                    onChange={(e) => handleInputChange('requiresFullPayment', e.target.checked)}
                  />
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Permite pagos en cuotas</FormLabel>
                  <Switch
                    isChecked={formData.installmentsAllowed}
                    onChange={(e) => handleInputChange('installmentsAllowed', e.target.checked)}
                  />
                </FormControl>
              </Stack>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {modalMode === 'create' ? 'Crear Plan de Pago' : 'Actualizar Plan de Pago'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
